import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import api, { getToken, setToken, removeToken } from '../lib/api';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const checkAuth = useCallback(async () => {
        // CRITICAL: If returning from OAuth callback, skip the /me check.
        if (window.location.hash?.includes('session_id=')) {
            setLoading(false);
            return;
        }

        // Check if we have a token
        const token = getToken();
        if (!token) {
            // No token - try to restore user from localStorage for display only
            const storedUser = localStorage.getItem('user');
            if (storedUser) {
                // We have user data but no token - clear it
                localStorage.removeItem('user');
            }
            setLoading(false);
            return;
        }

        // We have a token - verify it with backend
        try {
            const response = await api.get('/auth/me');
            setUser(response.data);
            localStorage.setItem('user', JSON.stringify(response.data));
        } catch (err) {
            // Token invalid or expired
            removeToken();
            localStorage.removeItem('user');
            setUser(null);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        checkAuth();
    }, [checkAuth]);

    /**
     * Login with Google OAuth
     * REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
     */
    const loginWithGoogle = () => {
        const redirectUrl = window.location.origin + '/dashboard';
        window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
    };

    /**
     * Legacy login with email/password
     * Stores JWT token for session persistence
     */
    const login = async (email, password) => {
        setError(null);
        try {
            const response = await api.post('/auth/login', { email, password });
            const { access_token, user: userData } = response.data;
            
            // Store token and user data
            setToken(access_token);
            localStorage.setItem('user', JSON.stringify(userData));
            setUser(userData);
            
            // If professional, also store pro_token for ProDashboard compatibility
            if (userData.role === 'professional') {
                localStorage.setItem('pro_token', access_token);
            }
            
            return userData;
        } catch (err) {
            const message = err.response?.data?.detail || 'Erreur de connexion';
            setError(message);
            throw new Error(message);
        }
    };

    /**
     * Legacy register with email/password
     * Creates admin with agency - now returns pending confirmation status
     */
    const register = async (userData, agencyData) => {
        setError(null);
        try {
            const response = await api.post('/auth/register', { 
                ...userData, 
                name: agencyData.name,
                siret: agencyData.siret,
                address: agencyData.address,
                city: agencyData.city,
                postal_code: agencyData.postal_code,
                agency_email: agencyData.email,
                agency_phone: agencyData.phone,
                logo_url: agencyData.logo_url
            });
            
            // Registration now returns pending status, not auto-login
            return response.data;
        } catch (err) {
            const errorData = err.response?.data;
            const message = errorData?.detail || "Erreur lors de l'inscription";
            const errorCode = errorData?.code;
            
            const error = new Error(message);
            error.code = errorCode;
            error.response = err.response;
            setError(message);
            throw error;
        }
    };

    /**
     * Login directly with a JWT token (post-registration auto-login).
     * Utilisé par le flow /register/solo pour éviter un 2ᵉ appel réseau.
     */
    const loginWithToken = async (accessToken, userData = null) => {
        setError(null);
        try {
            setToken(accessToken);
            if (userData) {
                localStorage.setItem('user', JSON.stringify(userData));
                setUser(userData);
                return userData;
            }
            // Sinon on récupère le profil complet via /auth/me
            const res = await api.get('/auth/me');
            const me = res.data;
            localStorage.setItem('user', JSON.stringify(me));
            setUser(me);
            return me;
        } catch (err) {
            const message = err.response?.data?.detail || 'Session invalide';
            setError(message);
            throw new Error(message);
        }
    };

    /**
     * Logout - clears session and token
     */
    const logout = async () => {
        try {
            await api.post('/auth/logout');
        } catch (err) {
            console.error('Logout error:', err);
        } finally {
            removeToken();
            localStorage.removeItem('user');
            setUser(null);
        }
    };

    /**
     * Set session from external auth (OAuth, email confirmation)
     */
    const setSession = (token, userData) => {
        setToken(token);
        localStorage.setItem('user', JSON.stringify(userData));
        setUser(userData);
    };

    const value = {
        user,
        loading,
        error,
        login,
        loginWithGoogle,
        loginWithToken,
        register,
        logout,
        checkAuth,
        setSession,
        isAuthenticated: !!user,
        isAdmin: user?.role === 'admin',
        isAgent: user?.role === 'agent',
        isSeller: user?.role === 'seller',
        isSuperAdmin: user?.role === 'super_admin',
        isSoloAgent: user?.role === 'agent' && !!user?.is_solo,
    };

    return (
        <AuthContext.Provider value={value}>
            {children}
        </AuthContext.Provider>
    );
}

export function useAuth() {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
}
